import { FunctionComponent } from 'react';
import { localizeDateTime } from 'utils/l10n';
import { Badge } from 'ui/badge';
import { HelpTooltip } from 'ui/tooltip';
import { colors } from 'ui/theme';
import {
  PredictiveAudienceGoal,
  ModelQuality,
  PreviousStrengthStringProps,
  GoalStatus,
} from '../types';
import {
  MarginlessBoldSubtitle2,
  BottomMarginBoldSubtitle2,
  CenterAlignedText,
  DetailHeaderWithMoreInfo,
  DetailSection,
  DetailsWrapper,
  EmptyDescription,
  InfoText,
  InfoWrapper,
  PrevStrengthBoldText,
  PrevStrengthText,
  ShortenedDivider,
  StyledMonospace,
} from './Styled';
import {
  GOAL_STATUS_BADGE_CONFIG,
  LAST_REFRESH_TOOLTIP,
  PREDICTIVE_STRENGTH_TOOLTIP,
  TEST_IDS,
  USER_PROFILE_FIELD,
} from '../constants';
import { ensureString, parseLastRefreshForDisplay, removeTimeZoneFromDateStamp } from '../utils';
import { PredictiveStrengthMeterIcon } from './PredictiveStrengthMeterIcon';
import { XAILeftNavCard } from './xai/XAILeftNavCard';

export type GoalDetailsPanelProps = {
  goal: PredictiveAudienceGoal;
  showOnlyCoreDetails?: boolean;
};

const displayText = {
  headers: {
    contactPropertyName: USER_PROFILE_FIELD,
    description: 'Description',
    status: 'Status',
    predictiveStrength: 'Predictive strength',
    lastRefresh: 'Last refresh',
  },
  default: {
    description: 'Optional description text goes here',
  },
  toggleTips: {
    predictiveStrength: <CenterAlignedText>{PREDICTIVE_STRENGTH_TOOLTIP}</CenterAlignedText>,
    lastRefresh: <CenterAlignedText>{LAST_REFRESH_TOOLTIP}</CenterAlignedText>,
  },
};

export const PreviousStrengthString = ({
  currentStrength,
  previousStrength,
  lastTrainedDate,
}: PreviousStrengthStringProps) => {
  const getPredictiveStrengthRank = {
    [ModelQuality.Strong]: 3,
    [ModelQuality.Satisfactory]: 2,
    [ModelQuality.Weak]: 1,
  };

  if (previousStrength && currentStrength && lastTrainedDate) {
    const prettifiedDate = localizeDateTime(new Date(removeTimeZoneFromDateStamp(lastTrainedDate)));

    const previousStrengthRank =
      getPredictiveStrengthRank[previousStrength as keyof typeof ModelQuality];
    const currentStrengthRank =
      getPredictiveStrengthRank[currentStrength as keyof typeof ModelQuality];

    let prefix;
    if (previousStrengthRank === currentStrengthRank) {
      prefix = 'Previously';
    } else if (currentStrengthRank < previousStrengthRank) {
      prefix = 'Reduced from';
    } else if (currentStrengthRank > previousStrengthRank) {
      prefix = 'Increased from';
    }

    return (
      <PrevStrengthText data-test={`${TEST_IDS.PreviousStrengthText}`}>
        {`${prefix}`}
        <PrevStrengthBoldText>{` '${previousStrength}' `}</PrevStrengthBoldText>
        {` on ${prettifiedDate}`}
      </PrevStrengthText>
    );
  } else {
    return null;
  }
};

export const GoalDetailsPanel: FunctionComponent<React.PropsWithChildren<GoalDetailsPanelProps>> = (
  props,
) => {
  const { goal, showOnlyCoreDetails = false } = props;
  const { description, modelDetails, lastScoredAt, status, userProfileField, lastTrainedAt } = goal;
  const goalStatus = status ? (
    <Badge variant={GOAL_STATUS_BADGE_CONFIG[status].variant}>
      {GOAL_STATUS_BADGE_CONFIG[status].text}
    </Badge>
  ) : (
    ''
  );

  const predictiveStrength = ensureString(modelDetails?.model_quality);
  const previousPredictiveStrength = ensureString(modelDetails?.previous_model_quality);
  const lastTrainedDate = ensureString(lastTrainedAt?.timestamp);
  const isXAILeftNavCardDisplayed = status === GoalStatus.READY || status === GoalStatus.REFRESHING;

  const predictiveStrengthElement = !showOnlyCoreDetails && (
    <>
      <ShortenedDivider />
      <DetailSection>
        <DetailHeaderWithMoreInfo>
          <MarginlessBoldSubtitle2>
            {displayText.headers.predictiveStrength}
          </MarginlessBoldSubtitle2>
          <HelpTooltip
            tooltipContent={displayText.toggleTips.predictiveStrength}
            iconProps={{
              color: colors.secondaryText,
            }}
          />
        </DetailHeaderWithMoreInfo>
        <InfoWrapper>
          <InfoText>{predictiveStrength}</InfoText>
          <PredictiveStrengthMeterIcon strength={predictiveStrength} />
        </InfoWrapper>
        <PreviousStrengthString
          currentStrength={predictiveStrength}
          previousStrength={previousPredictiveStrength}
          lastTrainedDate={lastTrainedDate}
        />
      </DetailSection>
    </>
  );

  const parsedLastRefresh = parseLastRefreshForDisplay(ensureString(lastScoredAt?.timestamp));

  const lastRefreshElement = !showOnlyCoreDetails && (
    <>
      <ShortenedDivider />
      <DetailSection>
        <DetailHeaderWithMoreInfo>
          <MarginlessBoldSubtitle2>{displayText.headers.lastRefresh}</MarginlessBoldSubtitle2>
          <HelpTooltip
            tooltipContent={displayText.toggleTips.lastRefresh}
            iconProps={{
              color: colors.secondaryText,
            }}
          />
        </DetailHeaderWithMoreInfo>
        <InfoText>{parsedLastRefresh}</InfoText>
      </DetailSection>
    </>
  );

  return (
    <DetailsWrapper>
      <DetailSection>
        <BottomMarginBoldSubtitle2>
          {displayText.headers.contactPropertyName}
        </BottomMarginBoldSubtitle2>
        <StyledMonospace>{userProfileField}</StyledMonospace>
      </DetailSection>
      <DetailSection>
        <BottomMarginBoldSubtitle2>{displayText.headers.description}</BottomMarginBoldSubtitle2>
        {description ? (
          <InfoText>{description}</InfoText>
        ) : (
          <EmptyDescription>No description</EmptyDescription>
        )}
      </DetailSection>
      <DetailSection>
        <BottomMarginBoldSubtitle2>{displayText.headers.status}</BottomMarginBoldSubtitle2>
        {goalStatus}
      </DetailSection>
      {predictiveStrengthElement}
      {lastRefreshElement}
      {isXAILeftNavCardDisplayed && <XAILeftNavCard goal={goal} />}
    </DetailsWrapper>
  );
};
